import { InputNumber } from 'antd';
import { FC } from 'react';
import type { PreferenceEntityType } from '../../services/preferences';
import './EntityPreferenceMenu.scss';

const PRESETS = [0.1, 0.2, 0.3, 0.5, 0.75, 1, 1.5, 2, 3, 5, 10];

const ENTITY_LABELS: Record<PreferenceEntityType, { singular: string; plural: string }> = {
  work: { singular: 'work', plural: 'works' },
  recording: { singular: 'recording', plural: 'recordings' },
  composer: { singular: 'composer', plural: 'composers' },
  artist: { singular: 'artist', plural: 'artists' },
};

const formatDisplayValue = (val?: string | number) => {
  if (val === undefined || val === null) return '';
  const asNumber = typeof val === 'number' ? val : Number(val);
  if (!Number.isFinite(asNumber)) {
    return typeof val === 'string' ? val : '';
  }
  return asNumber.toString();
};

const parseInputValue = (val?: string | number): string | number => {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const cleaned = val.replace(/,/g, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : '';
  }
  return '';
};

type EntityPreferenceMenuProps = {
  entityType: PreferenceEntityType;
  value: number;
  disabled?: boolean;
  isMuted: boolean;
  mutePending?: boolean;
  onToggleMute: () => void;
  onChange: (next: number) => void;
  onApply: () => void;
  onCancel: () => void;
};

/**
 * A reusable preference menu component for adjusting entity weights and muting.
 * 
 * IMPORTANT: This component uses the `.entity-preference-menu` class, which is
 * checked by the `isClickInsideSubpanel()` utility function. If your component
 * has custom click-outside handlers, use that utility to prevent closing dropdowns
 * when users interact with this menu.
 * 
 * The className `.entity-preference-menu` is used by `isClickInsideSubpanel()`
 * to identify clicks inside preference menus. Custom click-outside handlers
 * should check for this class or use the utility function.
 */
export const EntityPreferenceMenu: FC<EntityPreferenceMenuProps> = ({
  entityType,
  value,
  disabled,
  isMuted,
  mutePending,
  onToggleMute,
  onChange,
  onApply,
  onCancel,
}) => {
  const entityLabel = ENTITY_LABELS[entityType].singular;

  const normalizedValue = Number.isFinite(value) ? value : 1;

  const findNextPreset = (current: number) => PRESETS.find((preset) => preset > current);
  const findPrevPreset = (current: number) => {
    for (let i = PRESETS.length - 1; i >= 0; i -= 1) {
      if (PRESETS[i] < current) return PRESETS[i];
    }
    return undefined;
  };

  const stepUpDisabled = normalizedValue >= PRESETS[PRESETS.length - 1];
  const stepDownDisabled = normalizedValue <= PRESETS[0];

  return (
    <div
      className='entity-preference-menu'
      onClick={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className='entity-preference-menu__mute-row'>
        <button
          type='button'
          className='entity-preference-menu__mute-button'
          disabled={mutePending}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (mutePending) return;
            onToggleMute();
          }}
        >
          {isMuted ? `Unmute ${entityLabel}` : `Mute ${entityLabel}`}
        </button>
      </div>
      <div className='entity-preference-menu__label'>Adjust {entityLabel} frequency</div>
      <div className='entity-preference-menu__input-row'>
        <div className='entity-preference-menu__stepper'>
          <button
            type='button'
            className='entity-preference-menu__stepper-button'
            disabled={stepDownDisabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.preventDefault();
              if (stepDownDisabled) return;
              const next = findPrevPreset(normalizedValue);
              if (typeof next === 'number') {
                onChange(next);
              }
            }}
          >
            -
          </button>
          <InputNumber
            step={0.05}
            value={value}
            size='small'
            disabled={disabled}
            formatter={formatDisplayValue}
            parser={parseInputValue}
            onChange={(next) => {
              if (typeof next === 'number') {
                onChange(next);
              }
            }}
            onFocus={(event) => {
              const input = event.target as HTMLInputElement | null;
              if (!input) return;
              // Defer to let focus settle, then select all for quick overwrite.
              setTimeout(() => input.select(), 0);
            }}
            onPressEnter={() => {
              if (disabled) return;
              onApply();
            }}
          />
          <button
            type='button'
            className='entity-preference-menu__stepper-button'
            disabled={stepUpDisabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.preventDefault();
              if (stepUpDisabled) return;
              const next = findNextPreset(normalizedValue);
              if (typeof next === 'number') {
                onChange(next);
              }
            }}
          >
            +
          </button>
        </div>
      </div>
      <div className='entity-preference-menu__actions'>
        <button
          type='button'
          className='entity-preference-menu__cancel'
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onCancel();
          }}
        >
          Cancel
        </button>
        <button
          type='button'
          className='entity-preference-menu__apply'
          disabled={disabled}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (disabled) return;
            onApply();
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

EntityPreferenceMenu.displayName = 'EntityPreferenceMenu';
